<!--
  Pie del sitio. Dos decisiones que no son obvias leyendo el markup:

  1. El fondo usa `bg-artstgo-black` (#010101) y no negro puro. Es el mismo
     token que la franja de partners, que va justo encima: ambos igualan el
     negro quemado de los logos de sponsors, y compartirlo evita una costura
     visible en el borde entre las dos zonas (ABC-374, ABC-391).
  2. El contenido vive dentro del mismo contenedor centrado (max-w-7xl) que usa
     el resto del sitio. Sin él, en pantallas anchas el footer se estiraba a
     todo el viewport mientras la página quedaba centrada.
-->
<footer class="bg-artstgo-black text-white">
  <div
    class="mx-auto flex max-w-7xl flex-col gap-12 px-6 py-12 sm:gap-14 sm:px-8 lg:py-16"
  >
    <!-- Marca + navegación -->
    <!--
      Columnas de ritmo regular, alineadas desde la izquierda. Antes el logo iba
      a un extremo y la navegación al otro (`justify-between`), lo que dejaba el
      55% del ancho vacío EN EL MEDIO; el cliente lo leyó como desorden. Ahora el
      espacio sobrante queda al final, donde se lee como margen (ABC-382).
    -->
    <div
      class="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 sm:gap-x-10 lg:grid-cols-4"
    >
      <a
        routerLink="/"
        aria-label="Art Stgo, ir al inicio"
        class="col-span-2 w-28 rounded transition-opacity sm:col-span-1 duration-200 hover:opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary motion-reduce:transition-none"
      >
        <img
          class="w-full"
          src="/images/logo/art-stgo-logo-white.png"
          alt="Art Stgo"
        />
      </a>

      <div class="contents">
        <nav aria-labelledby="footer-explore-title">
          <h2
            id="footer-explore-title"
            class="mb-4 font-display text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-neutral-400"
          >
            Explorar
          </h2>
          <ul class="flex flex-col gap-0.5 text-sm">
            @for (item of exploreLinks; track item.path) {
              <li>
                <a
                  [routerLink]="item.path"
                  routerLinkActive="!text-primary"
                  #rla="routerLinkActive"
                  [attr.aria-current]="rla.isActive ? 'page' : null"
                  [class]="linkClass"
                  >{{ item.label }}</a
                >
              </li>
            }
          </ul>
        </nav>

        <nav aria-labelledby="footer-access-title">
          <h2
            id="footer-access-title"
            class="mb-4 font-display text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-neutral-400"
          >
            Accesos
          </h2>
          <ul class="flex flex-col gap-0.5 text-sm">
            <li>
              <a data-access-link routerLink="/auth/sign-in" [class]="linkClass"
                >Personas y coleccionistas</a
              >
            </li>
            @if (accessUrls.artistPanelUrl) {
              <li>
                <a
                  data-access-link
                  [href]="accessUrls.artistPanelUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  [class]="linkClass"
                  >Artistas</a
                >
              </li>
            }
          </ul>
        </nav>

        <!--
          La app tenía su propia sección: antes App Store y Google Play eran dos
          enlaces de texto perdidos entre accesos administrativos, pese a ser un
          objetivo del negocio (hay app publicada y un modal que la promueve).
        -->
        @if (accessUrls.appStoreUrl || accessUrls.playStoreUrl) {
          <nav aria-labelledby="footer-app-title">
            <h2
              id="footer-app-title"
              class="mb-4 font-display text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-neutral-400"
            >
              La app
            </h2>
            <p class="mb-3 max-w-[22ch] text-sm text-neutral-400">
              Sigue a tus artistas y vota en los concursos desde el celular.
            </p>
            <!--
              Distintivos OFICIALES de cada tienda, descargados de sus recursos
              de marca y sin alterar: Apple y Google lo exigen y prohíben
              recrearlos, recolorearlos o deformarlos.

              Los dos alto distinto a propósito. El PNG de Google trae 29px de
              área de resguardo arriba y abajo dentro del lienzo (646×250, con
              el distintivo visible de 646×192); el SVG de Apple viene al ras.
              Igualar el atributo `height` los dejaría de tamaños distintos a la
              vista, así que el de Google va 1.3× para que ambos rectángulos
              negros midan lo mismo (ABC-394).
            -->
            <ul class="flex flex-col items-start gap-3 text-sm">
              @if (accessUrls.appStoreUrl) {
                <li>
                  <a
                    data-access-link
                    [href]="accessUrls.appStoreUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-block rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
                  >
                    <img
                      src="images/stores/app-store-es.svg"
                      alt="Descarga Art Stgo en el App Store"
                      width="120"
                      height="40"
                      class="h-10 w-auto"
                      loading="lazy"
                    />
                  </a>
                </li>
              }
              @if (accessUrls.playStoreUrl) {
                <li>
                  <a
                    data-access-link
                    [href]="accessUrls.playStoreUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-block rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
                  >
                    <img
                      src="images/stores/google-play-es.png"
                      alt="Descarga Art Stgo en Google Play"
                      width="134"
                      height="52"
                      class="h-[3.25rem] w-auto"
                      loading="lazy"
                    />
                  </a>
                </li>
              }
            </ul>
          </nav>
        }
      </div>
    </div>

    <!--
      Cierre. "Equipo Art Stgo" vive acá y no entre los accesos de usuario: es
      la puerta del equipo, no un destino para quien visita el sitio.
    -->
    <div
      class="flex flex-col gap-3 border-t border-neutral-900 pt-6 text-xs text-neutral-400 sm:flex-row sm:items-center sm:justify-between sm:pt-8"
    >
      <p>© {{ currentYear }} Art Stgo. Todos los derechos reservados.</p>
      @if (accessUrls.adminPanelUrl) {
        <a
          data-access-link
          [href]="accessUrls.adminPanelUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="-mx-2 self-start rounded px-2 py-1 text-neutral-400 transition-colors duration-200 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none sm:self-auto"
          >Equipo Art Stgo</a
        >
      }
    </div>
  </div>
</footer>
